/**
 * Mega-menu "Shop op moment".
 *
 * Twee componenten:
 *   details-moment-mega  - het desktoppaneel, erft van DetailsMega uit header.js
 *   moment-gender        - de segmented control in de drawer
 *
 * Dit bestand moet ná header.js staan: DetailsMega is een top-level class-binding
 * uit een classic script, en defer-scripts draaien in documentvolgorde.
 */

class MomentMega extends DetailsMega {
  constructor() {
    super();

    this.openTimer = null;
    this.closeTimer = null;

    // Escape terwijl de focus buiten het paneel staat (na openen op hover) wordt
    // door de basisklasse niet opgepakt: die zoekt vanaf event.target omhoog.
    this.handleMomentEscape = this.handleMomentEscape.bind(this);
    document.addEventListener('keydown', this.handleMomentEscape);

    this.addEventListener('keydown', this.handleArrowKeys.bind(this));
  }

  disconnectedCallback() {
    if (super.disconnectedCallback) super.disconnectedCallback();
    this.clearTimers();
    document.removeEventListener('keydown', this.handleMomentEscape);
  }

  get openOnHover() {
    return this.hasAttribute('data-open-on-hover');
  }

  get hoverDelay() {
    return parseInt(this.dataset.hoverDelay, 10) || 120;
  }

  clearTimers() {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
  }

  /** De andere mega-menu-triggers in dezelfde header. */
  get siblings() {
    const header = this.closest('.header') || document;
    return Array.from(header.querySelectorAll('details[is="details-moment-mega"]')).filter(
      (el) => el !== this
    );
  }

  /**
   * Vervangt de directe hover van DetailsDropdown door een intentievertraging, zodat
   * het paneel niet openklapt als je er alleen langs beweegt. Bij het verlaten een
   * kortere coulance, anders flikkert het bij de sprong van balk naar paneel.
   *
   * Staat er al een paneel open, dan wisselt het menu direct - zonder vertraging.
   * Je hebt je intentie dan immers al getoond, en wachten voelt daar als hapering.
   */
  detectHover(event) {
    if (!this.openOnHover) return;
    if (FoxTheme.config.isTouch) return;
    // Let op: FoxTheme.config.mqlTablet is een boolean, geen MediaQueryList.
    // `.matches` erop is altijd undefined en zou de guard stilzwijgend uitschakelen.
    if (FoxTheme.config.mqlTablet === true) return;

    this.clearTimers();

    if (event.type !== 'mouseenter') {
      this.closeTimer = setTimeout(() => {
        this.open = false;
      }, 200);
      return;
    }

    this.openFromHover();
  }

  /**
   * Openen vanuit hover. Staat er al een paneel open, dan wisselt het menu direct
   * en zonder schuifanimatie: het paneel blijft immers staan, alleen de inhoud
   * wisselt. De intentievertraging geldt daarom alleen voor de eerste opening.
   */
  openFromHover() {
    if (this.open) return;

    // Timers van alle triggers opruimen, niet alleen die van de openstaande.
    // Bij snel heen en weer bewegen bleef er anders een openTimer hangen die
    // even later alsnog het verkeerde paneel opende.
    const siblings = this.siblings;
    siblings.forEach((el) => el.clearTimers());

    const openSibling = siblings.find((el) => el.open);

    if (openSibling) {
      openSibling.switching = true;
      this.switching = true;
      openSibling.open = false;
      this.open = true;
      return;
    }

    this.openTimer = setTimeout(() => {
      this.open = true;
    }, this.hoverDelay);
  }

  /**
   * Openen en sluiten houden de schuifanimatie van DetailsMega. Wisselen tussen
   * Dames en Heren gebeurt zonder overgang: het paneel blijft staan en alleen de
   * inhoud verandert, dus elke animatie leest daar als hapering.
   */
  async showWithTransition() {
    if (!this.switching) return super.showWithTransition();
    this.switching = false;
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      { visibility: 'visible', transform: 'translateY(0)' },
      { duration: 0 }
    ).finished;
  }

  async hideWithTransition() {
    if (!this.switching) return super.hideWithTransition();
    this.switching = false;
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      { visibility: 'hidden', transform: 'translateY(-100%)' },
      { duration: 0 }
    ).finished;
  }

  handleSummaryClick(event) {
    this.siblings.forEach((el) => {
      if (!el.open) return;
      el.clearTimers();
      el.open = false;
    });
    super.handleSummaryClick(event);
  }

  handleMomentEscape(event) {
    if (event.code !== 'Escape' || !this.open) return;
    if (this.contains(event.target)) return;

    this.clearTimers();
    this.open = false;
    this.summaryElement.focus();
  }

  /**
   * Pijltjes binnen het paneel. Home/End springen naar de eerste en laatste link.
   */
  handleArrowKeys(event) {
    if (!this.open) return;
    if (['ArrowDown', 'ArrowUp', 'Home', 'End'].indexOf(event.key) === -1) return;

    const links = Array.from(this.querySelectorAll('.mega-menu a[href]')).filter(
      (link) => link.offsetParent !== null
    );
    if (!links.length) return;

    const current = links.indexOf(document.activeElement);
    let next;

    if (event.key === 'Home') {
      next = 0;
    } else if (event.key === 'End') {
      next = links.length - 1;
    } else if (current === -1) {
      next = 0;
    } else {
      next = event.key === 'ArrowDown' ? current + 1 : current - 1;
      if (next < 0 || next >= links.length) return;
    }

    event.preventDefault();
    links[next].focus();
  }
}

customElements.define('details-moment-mega', MomentMega, { extends: 'details' });

/**
 * Vangnet voor het wisselen. mouseenter vuurt maar één keer bij binnenkomst; raakt
 * hij verloren doordat er net een paneel open- of dichtging, dan blijft het
 * verkeerde paneel staan. Deze gedelegeerde mouseover corrigeert dat: sta je met
 * de muis op een trigger terwijl een ander paneel openstaat, dan wisselt hij.
 */
document.addEventListener('mouseover', (event) => {
  const summary = event.target.closest && event.target.closest('.moment-mega > summary');
  if (!summary) return;

  const details = summary.parentElement;
  if (typeof details.openFromHover !== 'function') return;
  if (!details.hasAttribute('data-open-on-hover')) return;
  if (FoxTheme.config.isTouch || FoxTheme.config.mqlTablet === true) return;
  if (details.open) return;

  const anderOpen = [...document.querySelectorAll('details[is="details-moment-mega"]')].some(
    (el) => el !== details && el.open
  );
  if (anderOpen) details.openFromHover();
});

/**
 * Segmented control Dames | Heren in de drawer.
 *
 * De radio-inputs doen het werk al zonder JavaScript via :checked. Deze component
 * voegt twee dingen toe die CSS niet kan:
 *   - inert op het inactieve paneel. getFocusableElements van het thema filtert niet
 *     op zichtbaarheid, dus zonder inert belandt het verborgen paneel in de focus
 *     trap van de drawer.
 *   - de focus trap opnieuw laten berekenen, omdat trapFocus first/last maar één keer
 *     bepaalt bij het openen van de drawer.
 *
 * Bewust géén scroll-code: de twee panelen liggen in dezelfde gridcel, dus de hoogte
 * verandert niet bij het wisselen en de scrollpositie blijft vanzelf staan.
 */
class MomentGender extends HTMLElement {
  connectedCallback() {
    this.inputs = Array.from(this.querySelectorAll('.moment-switch__input'));
    this.panes = {
      women: this.querySelector('.moment-switch__pane--women'),
      men: this.querySelector('.moment-switch__pane--men'),
    };

    this.addEventListener('change', this.handleChange.bind(this));

    const checked = this.inputs.find((input) => input.checked);
    this.apply(checked ? checked.value : 'women', false);
  }

  handleChange(event) {
    if (!event.target.classList.contains('moment-switch__input')) return;
    this.apply(event.target.value, true);
  }

  apply(gender, refocus) {
    this.dataset.gender = gender;

    Object.keys(this.panes).forEach((key) => {
      const pane = this.panes[key];
      if (!pane) return;
      pane.inert = key !== gender;
    });

    if (!refocus) return;

    const drawer = this.closest('menu-drawer');
    if (drawer && FoxTheme.a11y && document.activeElement) {
      FoxTheme.a11y.trapFocus(drawer, document.activeElement);
    }
  }
}

customElements.define('moment-gender', MomentGender);
